import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  BarChart3,
  Download,
  Upload,
  TrendingUp,
  RefreshCw,
  Mail,
  PieChart as PieChartIcon,
  Clock,
  Database
} from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { apiService } from '../config/api'
import toast from 'react-hot-toast'
import WelcomeGuide from '../components/WelcomeGuide'

const COLORS = ['#8b5cf6', '#ef4444', '#0ea5e9', '#f59e0b', '#22c55e', '#64748b']

function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) {
  if (value === 0) return null;
  const RADIAN = Math.PI / 180;
  const isSmall = percent < 0.12;
  const baseRadius = isSmall ? 40 : 20;
  const x = cx + (outerRadius + baseRadius) * Math.cos(-midAngle * RADIAN);
  let y = cy + (outerRadius + baseRadius) * Math.sin(-midAngle * RADIAN);

  if (isSmall) {
    const quadrant = Math.floor(((midAngle % 360) + 360) % 360 / 90);
    if (quadrant === 0 || quadrant === 2) {
      y += (index % 2 ? 15 : -15);
    } else {
      y += (index % 2 ? 12 : -12);
    }
  }

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const textColor = isDark ? '#94a3b8' : '#64748b';

  return (
    <text
      x={x}
      y={y}
      fill={textColor}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
}

function renderPieLabelLine({ cx, cy, midAngle, outerRadius, percent, index }) {
  const RADIAN = Math.PI / 180;
  const isSmall = percent < 0.12;
  const baseRadius = isSmall ? 35 : 15;
  const sx = cx + outerRadius * Math.cos(-midAngle * RADIAN);
  const sy = cy + outerRadius * Math.sin(-midAngle * RADIAN);
  const ex = cx + (outerRadius + baseRadius) * Math.cos(-midAngle * RADIAN);
  let ey = cy + (outerRadius + baseRadius) * Math.sin(-midAngle * RADIAN);

  if (isSmall) {
    const quadrant = Math.floor(((midAngle % 360) + 360) % 360 / 90);
    if (quadrant === 0 || quadrant === 2) {
      ey += (index % 2 ? 15 : -15);
    } else {
      ey += (index % 2 ? 12 : -12);
    }
  }

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const stroke = isDark ? '#334155' : '#e2e8f0';

  return (
    <path d={`M${sx},${sy}L${ex},${ey}`} stroke={stroke} strokeWidth={1} fill="none" />
  );
}

export default function Dashboard() {
  const [jobs, setJobs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [processedCounts, setProcessedCounts] = useState({ processed: 0, total: 0 })
  const [isCountsLoading, setIsCountsLoading] = useState(true)
  const [isRepairing, setIsRepairing] = useState(false);
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);

  useEffect(() => {
    loadDashboardData(true);
    loadProcessedCounts(true);
    checkForFreshInstallation();
    const interval = setInterval(() => {
      loadDashboardData(false);
      loadProcessedCounts(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const checkForFreshInstallation = async () => {
    try {
      const response = await fetch('/api/onboarding/status', {
        credentials: 'include',
        headers: { 'x-api-token': localStorage.getItem('api_token') || '' }
      });
      if (!response.ok) return;

      const data = await response.json();
      const onboardingStatus = data.data;

      if (!onboardingStatus.hasSeenWelcomeGuide && jobs.length === 0) {
        setTimeout(() => setShowWelcomeGuide(true), 1000);
      }
    } catch (error) { }
  };

  const handleWelcomeGuideClose = async () => {
    setShowWelcomeGuide(false);
    try {
      await fetch('/api/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-token': localStorage.getItem('api_token') || '' },
        credentials: 'include',
        body: JSON.stringify({ hasSeenWelcomeGuide: true, hasSeenOnboarding: true })
      });
    } catch (error) { }
  };

  const loadDashboardData = async (showLoading = false) => {
    try {
      if (showLoading) setIsLoading(true);
      const jobsResponse = await apiService.getJobs({ limit: 20, offset: 0 });
      const responseData = jobsResponse.data.data || {};
      const newJobs = Array.isArray(responseData) ? responseData : (responseData.jobs || []);

      setJobs(prevJobs => {
        if (JSON.stringify(prevJobs) !== JSON.stringify(newJobs)) return newJobs;
        return prevJobs;
      });
    } catch (error) {
      if (showLoading) toast.error('Failed to load dashboard data');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const loadProcessedCounts = async (showLoading = false) => {
    try {
      if (showLoading) setIsCountsLoading(true);
      const response = await apiService.getProcessedCounts();
      const newCounts = response.data.data || { processed: 0, total: 0 };
      setProcessedCounts(newCounts);
    } catch (error) {
      if (showLoading) toast.error('Failed to load stats');
    } finally {
      if (showLoading) setIsCountsLoading(false);
    }
  };

  const handleRepairStats = async () => {
    if (isRepairing) return;
    setIsRepairing(true);
    try {
      const response = await apiService.repairJobCounts();
      if (response.data.success) {
        toast.success('Statistics repaired successfully!');
        await Promise.all([loadDashboardData(true), loadProcessedCounts(true)]);
      }
    } catch (error) {
      toast.error('Error repairing statistics');
    } finally {
      setIsRepairing(false);
    }
  };

  const totalValid = jobs.reduce((sum, job) => sum + (parseInt(job.valid_count) || 0), 0)
  const totalInvalid = jobs.reduce((sum, job) => sum + (parseInt(job.invalid_count) || 0), 0)
  const totalCatchAll = jobs.reduce((sum, job) => sum + (parseInt(job.catch_all_count) || 0), 0)
  const totalDisposable = jobs.reduce((sum, job) => sum + (parseInt(job.disposable_count) || 0), 0)

  const chartData = [
    { name: 'Valid', value: totalValid, color: '#22c55e' },
    { name: 'Invalid', value: totalInvalid, color: '#ef4444' },
    { name: 'Catch All', value: totalCatchAll, color: '#8b5cf6' },
    { name: 'Disposable', value: totalDisposable, color: '#f59e0b' }
  ].filter(d => d.value > 0)

  const stats = [
    { name: 'Total Jobs', value: jobs.length, icon: TrendingUp, color: 'brand', bg: 'bg-brand-50 dark:bg-brand-500/10', text: 'text-brand-600 dark:text-brand-400' },
    { name: 'Processed Emails', value: processedCounts.processed, icon: Activity, color: 'primary', bg: 'bg-primary-50 dark:bg-primary-500/10', text: 'text-primary-600 dark:text-primary-400' },
    { name: 'Total Emails', value: processedCounts.total, icon: Mail, color: 'slate', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400' },
    { name: 'Valid Emails', value: totalValid, icon: CheckCircle, color: 'success', bg: 'bg-success-50 dark:bg-success-500/10', text: 'text-success-600 dark:text-success-400' },
    { name: 'Invalid Emails', value: totalInvalid, icon: XCircle, color: 'danger', bg: 'bg-danger-50 dark:bg-danger-500/10', text: 'text-danger-600 dark:text-danger-400' },
  ]

  if (isLoading || isCountsLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
            <div className="h-4 w-64 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
          </div>
          <div className="h-10 w-32 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
          <div className="h-80 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">Dashboard</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400 font-medium">
            Strategic overview of your verification ecosystem
          </p>
        </div>
        <button
          onClick={handleRepairStats}
          disabled={isRepairing}
          className="btn btn-secondary shadow-sm"
        >
          {isRepairing ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin text-brand-500" />
              Repairing System...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2 text-brand-500 font-bold" />
              Sync Statistics
            </>
          )}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="card group hover:-translate-y-1 transition-all duration-300">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">{stat.name}</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                    {stat.value.toLocaleString()}
                  </p>
                </div>
                <div className={`p-3 rounded-2xl ${stat.bg} ${stat.text} group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-xs font-medium text-slate-400">
                <TrendingUp className="h-3 w-3 mr-1 text-success-500" />
                <span>Live updates</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Chart Column */}
        <div className="lg:col-span-5">
          <div className="card h-full">
            <div className="card-header flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-brand-500" />
                Status Analysis
              </h3>
            </div>
            <div className="card-body">
              {chartData.length > 0 ? (
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        label={renderPieLabel}
                        labelLine={renderPieLabelLine}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] text-slate-400 font-medium">
                  <BarChart3 className="h-12 w-12 mb-3 opacity-20" />
                  <p>No verification data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Jobs Column */}
        <div className="lg:col-span-7">
          <div className="card h-full">
            <div className="card-header flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-brand-500" />
                Recent Activities
              </h3>
              <Link to="/results" className="text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400">View All</Link>
            </div>
            <div className="card-body p-0">
              <div className="overflow-x-auto">
                {jobs.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800">
                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-6">List Identity</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Outcome</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pr-6">Progress</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {jobs.slice(0, 6).map(job => (
                        <tr key={job.job_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                          <td className="px-6 py-4 pl-6">
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-brand-600 transition-colors">{job.job_name}</span>
                              <span className="text-xs text-slate-400 font-medium mt-0.5">{new Date(job.created_at).toLocaleDateString()}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              <span className={`px-3 py-1.5 rounded-xl text-[10px] font-extrabold uppercase tracking-widest shadow-sm ${job.status === 'completed' ? 'bg-success-50 text-success-700 border border-success-100 dark:bg-success-900/20 dark:text-success-400 dark:border-success-800/30' :
                                job.status === 'processing' ? 'bg-brand-50 text-brand-700 border border-brand-100 animate-pulse-slow dark:bg-brand-900/20 dark:text-brand-400 dark:border-brand-800/30' :
                                  'bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                                }`}>
                                {job.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                                {((job.processed_emails / job.total_emails) * 100).toFixed(0)}%
                              </span>
                              <div className="w-24 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full mt-2 overflow-hidden">
                                <div
                                  className="h-full bg-brand-500 rounded-full"
                                  style={{ width: `${(job.processed_emails / job.total_emails) * 100}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <Database className="h-10 w-10 mb-3 opacity-20" />
                    <p className="font-medium text-sm">Waiting for your first verification list</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showWelcomeGuide && (
        <WelcomeGuide onClose={handleWelcomeGuideClose} />
      )}
    </div>
  )
} 